import { FC } from 'react';
import { Box, TableCell, TableRow, Tooltip } from '@mui/material';
import { Link } from 'react-router';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ListTable from '../../components/ListTable';
import { colors, fontWeights } from '../../theme/tokens';
import RowActions, { DocumentAction, DOCUMENT_ACTIONS_WIDTH } from './RowActions';
import { HomeDocument } from './types';

// The "Documents table" of the XD common structure, on desktop and
// tablet, over the shared table (components/ListTable): orange header,
// rule on hover, actions column and loading / empty / error states.

export type RowResolvers = {
  archiverName: (document: HomeDocument) => string;
  serviceName: (document: HomeDocument) => string;
  formatDate: (value: string) => string;
  canWrite: (document: HomeDocument) => boolean;
};

type Props = {
  documents: HomeDocument[];
  isLoading: boolean;
  isError: boolean;
  hasFilters: boolean;
  resolvers: RowResolvers;
  onAction: (action: DocumentAction, document: HomeDocument) => void;
  onRetry: () => void;
};

export const COLUMNS = [
  'Nom du fichier',
  'Archiveur',
  'Service',
  'Emission',
  'Archivage',
] as const;

export const emptyDocumentsText = (hasFilters: boolean): string =>
  hasFilters
    ? 'Aucun document ne correspond à votre recherche.'
    : 'Aucun document archivé pour le moment.';

export const DOCUMENTS_ERROR_TEXT = 'Impossible de charger les documents.';

const DocumentsTable: FC<Props> = ({
  documents,
  isLoading,
  isError,
  hasFilters,
  resolvers,
  onAction,
  onRetry,
}) => (
  <ListTable
    ariaLabel="Documents"
    columns={COLUMNS}
    actionsWidth={DOCUMENT_ACTIONS_WIDTH}
    isLoading={isLoading}
    isError={isError}
    isEmpty={documents.length === 0}
    errorText={DOCUMENTS_ERROR_TEXT}
    emptyText={emptyDocumentsText(hasFilters)}
    onRetry={onRetry}
  >
    {documents.map((document) => (
      <TableRow key={document.id}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Link
              to={`/document/${document.id}`}
              style={{
                color: colors.textBase,
                fontWeight: fontWeights.medium,
                textDecoration: 'none',
              }}
            >
              {document.title}
            </Link>
            {document.confidential && (
              <Tooltip title="Confidentiel">
                <LockOutlinedIcon
                  fontSize="small"
                  sx={{ color: colors.muted }}
                  aria-label="Confidentiel"
                />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>{resolvers.archiverName(document)}</TableCell>
        <TableCell>{resolvers.serviceName(document)}</TableCell>
        <TableCell>{resolvers.formatDate(document.issuing_date)}</TableCell>
        <TableCell>{resolvers.formatDate(document.archiving_date)}</TableCell>
        <TableCell align="right">
          <RowActions
            document={document}
            canWrite={resolvers.canWrite(document)}
            variant="table"
            onAction={onAction}
          />
        </TableCell>
      </TableRow>
    ))}
  </ListTable>
);

export default DocumentsTable;
